import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Title,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import {
  IconActivity,
  IconEdit,
  IconHandStop,
  IconPlus,
} from "@tabler/icons-react";
import {
  MRT_ColumnDef,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import { useEffect, useMemo, useState } from "react";
import { repositoryMdm } from "../../../_base/_const/_constVar";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { _localization } from "../../../config/location";
import { MessageResponse } from "../../../model/MessageResponse";
import WarrantyCustomerGroupForm, {
  customerGroupTypes,
} from "./WarrantyCustomerGroupForm";

// Cấu hình nhóm khách hàng dùng ở màn lập phiếu tiếp nhận yêu cầu (TBL_WARRANTY_CUSTOMER_GROUP)
const WarrantyCustomerGroupList = () => {
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    const res = await repositoryMdm.get<MessageResponse<any[]>>(
      "/api/v1/TblWarrantyCustomerGroup/get-list",
    );
    setData(res?.success ? res.data : []);
    setIsLoading(false);
  };

  const openForm = (row?: any) => {
    modals.openConfirmModal({
      title: (
        <Title order={5}>
          {row ? "Chỉnh sửa nhóm khách hàng" : "Thêm nhóm khách hàng"}
        </Title>
      ),
      size: "auto",
      children: <WarrantyCustomerGroupForm data={row} onSaved={fetchData} />,
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });
  };

  // Không cho xóa nhóm (mã đã lưu trên phiếu bảo hành), chỉ bật/tắt sử dụng
  const toggleActive = async (row: any) => {
    const res = await repositoryMdm.post<MessageResponse<boolean>>(
      `/api/v1/TblWarrantyCustomerGroup/update-active?id=${row.id}&active=${!row.active}`,
    );
    if (res?.success) {
      NotificationExtension.Success("Cập nhật thành công !");
      fetchData();
    }
  };

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      { accessorKey: "code", header: "Mã nhóm", size: 100 },
      { accessorKey: "name", header: "Tên nhóm", size: 260 },
      {
        accessorKey: "attribute7",
        header: "Loại nhóm",
        size: 220,
        Cell: ({ row }) =>
          customerGroupTypes.find((e) => e.value === row.original.attribute7)
            ?.label ?? row.original.attribute7,
      },
      { accessorKey: "attribute6", header: "Số điện thoại", size: 130 },
      { accessorKey: "attribute5", header: "Địa chỉ cụ thể", size: 220 },
      {
        accessorKey: "active",
        header: "Trạng thái",
        size: 120,
        Cell: ({ row }) => (
          <Badge color={row.original.active ? "teal" : "red"} radius="sm">
            {row.original.active ? "Đang sử dụng" : "Dừng sử dụng"}
          </Badge>
        ),
      },
      {
        accessorKey: "action",
        header: "Thao tác",
        size: 100,
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <Box style={{ display: "flex", gap: "10px" }}>
            <Tooltip label="Chỉnh sửa">
              <ActionIcon
                variant="light"
                color="orange"
                onClick={() => openForm(row.original)}
              >
                <IconEdit size={20} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
            <Tooltip
              label={row.original.active ? "Dừng sử dụng" : "Sử dụng"}
            >
              <ActionIcon
                variant="light"
                color={row.original.active ? "violet" : "green"}
                onClick={() => toggleActive(row.original)}
              >
                {row.original.active ? (
                  <IconHandStop size={20} />
                ) : (
                  <IconActivity size={20} />
                )}
              </ActionIcon>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [],
  );

  useEffect(() => {
    fetchData();
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    localization: _localization,
    getRowId: (row) => String(row.id),
    renderTopToolbarCustomActions: () => (
      <Button
        leftSection={<IconPlus size={14} />}
        variant="outline"
        onClick={() => openForm()}
      >
        Thêm mới
      </Button>
    ),
    initialState: {
      density: "xs",
      pagination: { pageIndex: 0, pageSize: 50 },
    },
    enableStickyHeader: true,
    mantineTableContainerProps: {
      style: { maxHeight: "calc(100vh - 260px)" },
    },
    mantineTableBodyCellProps: {
      style: { fontWeight: 500, fontSize: "12.5px", padding: "5px 15px" },
    },
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    state: { isLoading, showProgressBars: isLoading },
  });

  return <MantineReactTable table={table} />;
};

export default WarrantyCustomerGroupList;
